/**
 * THE ONE TOOLTIP.
 *
 * Every chart on this screen renders `<Tooltip content={<DashTooltip .../>}>`.
 * No default recharts tooltip survives anywhere — the reports screen carried
 * five of them, styled inline with `contentStyle`, and a `chart-theme.test.ts`
 * source scan now fails if a sixth appears.
 *
 * WHAT IT SAYS, AND WHY EACH LINE IS THERE
 *
 *   · the point label
 *   · one row per VISIBLE series — a series the reader toggled off in the legend
 *     is not in the tooltip either, or the toggle is a lie
 *   · the value, with plural agreement from the measure's own noun pair
 *   · the delta against the comparison period, through the same <Delta> rules
 *     the KPI tiles use — a `live` figure gets no chip, an unlike pair gets no
 *     percentage, and a percentage against zero is shown as an absolute
 *   · the share of the total, because a count without its denominator cannot be
 *     sized
 *   · the record count behind the point, where a mark aggregates rows
 *   · and a NOTE, where the panel has to say what the numbers above it do NOT
 *     mean. Half the panels on this screen have one.
 */
import React from 'react';
import { TooltipProps } from 'recharts';
import { Delta } from './chart-delta';
import {
  TipCard,
  TipLabel,
  TipNote,
  TipRow,
  TipStrong,
  TipSwatch,
} from './chart.styled';
import { format } from './chart-theme';

export interface TooltipSeries {
  /** Matches the `dataKey` on the mark. */
  key: string;
  label: string;
  tone: string;
  dashed?: boolean;
  /** [singular, plural] from the measure. Falls back to no noun at all rather
   *  than inventing one. */
  noun?: [string, string];
  /** This series is the comparison period, so it renders without a delta of its
   *  own — it IS the delta's other half. */
  isCompare?: boolean;
}

export interface DashTooltipProps extends TooltipProps<number, string> {
  series: TooltipSeries[];
  /** Series keys the reader has toggled off. */
  hidden?: string[];
  /** Formats the x value into the tooltip's heading. */
  labelOf?: (raw: unknown, payload?: Record<string, unknown>) => string;
  /** The denominator for "share of total", when the panel has one. */
  total?: number;
  /** Whether the two windows are the same length. False suppresses percentages
   *  everywhere, which is the rule the whole screen obeys. */
  likeForLike?: boolean;
  curSpanMs?: number;
  prevSpanMs?: number;
  /** A sentence the panel has to add, rendered under the rows.
   *
   *  A FUNCTION when the sentence depends on the point — an anomaly's
   *  plain-language reason is the case this exists for. "2.4 sigma" is a
   *  statistic; "3.1x the usual, 412 against a recent average of 132" is
   *  something a manager can act on, and it can only be written per point. */
  note?: React.ReactNode | ((row: Record<string, unknown>) => React.ReactNode);
  /** How many records are behind this point, when a mark aggregates rows. */
  countOf?: (payload: Record<string, unknown>) => number | null;
}

const num = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const DashTooltip: React.FC<DashTooltipProps> = ({
  active,
  payload,
  label,
  series,
  hidden = [],
  labelOf,
  total,
  likeForLike = true,
  curSpanMs,
  prevSpanMs,
  note,
  countOf,
}) => {
  if (!active || !payload || !payload.length) return null;

  const row = (payload[0].payload || {}) as Record<string, unknown>;
  const heading = labelOf ? labelOf(label, row) : String(label ?? '');

  const visible = series.filter(s => !hidden.includes(s.key));
  if (!visible.length) return null;

  const compareKey = series.find(s => s.isCompare)?.key;
  const compareValue = compareKey ? num(row[compareKey]) : null;

  const records = countOf ? countOf(row) : null;
  const resolvedNote = typeof note === 'function' ? note(row) : note;

  return (
    <TipCard role="tooltip">
      <TipLabel>{heading}</TipLabel>

      {visible.map(entry => {
        const value = num(row[entry.key]);
        if (value == null) return null;

        return (
          <TipRow key={entry.key}>
            <TipSwatch tone={entry.tone} dashed={entry.dashed} />
            <TipStrong>{value.toLocaleString()}</TipStrong>
            {entry.noun ? (
              <span>{value === 1 ? entry.noun[0] : entry.noun[1]}</span>
            ) : (
              <span>{entry.label}</span>
            )}
            {/* The share, only where there is a denominator to be a share OF. */}
            {total != null && total > 0 && !entry.isCompare ? (
              <span>· {format.share(value, total)}</span>
            ) : null}
            {/* The delta, on the primary series only, and only when there is a
                comparison to make. The rules live in <Delta>. */}
            {!entry.isCompare && compareValue != null ? (
              <Delta
                delta={value - compareValue}
                compareValue={compareValue}
                likeForLike={likeForLike}
                curSpanMs={curSpanMs}
                prevSpanMs={prevSpanMs}
              />
            ) : null}
          </TipRow>
        );
      })}

      {records != null ? (
        <TipRow>
          <span>
            from {records.toLocaleString()} {records === 1 ? 'record' : 'records'}
          </span>
        </TipRow>
      ) : null}

      {resolvedNote ? <TipNote>{resolvedNote}</TipNote> : null}
    </TipCard>
  );
};

/**
 * A tooltip for a single-series categorical chart — a histogram bucket, a pie
 * wedge, a heat cell. Same card, no series list to reconcile.
 */
export interface DashSimpleTooltipProps extends TooltipProps<number, string> {
  tone: string;
  valueKey: string;
  labelKey: string;
  noun?: [string, string];
  total?: number;
  note?: React.ReactNode | ((row: Record<string, unknown>) => React.ReactNode);
}

export const DashSimpleTooltip: React.FC<DashSimpleTooltipProps> = ({
  active,
  payload,
  tone,
  valueKey,
  labelKey,
  noun,
  total,
  note,
}) => {
  if (!active || !payload || !payload.length) return null;
  const row = (payload[0].payload || {}) as Record<string, unknown>;
  const value = num(row[valueKey]) ?? 0;
  const resolved = typeof note === 'function' ? note(row) : note;

  return (
    <TipCard role="tooltip">
      <TipLabel>{String(row[labelKey] ?? '')}</TipLabel>
      <TipRow>
        <TipSwatch tone={tone} />
        <TipStrong>{value.toLocaleString()}</TipStrong>
        {noun ? <span>{value === 1 ? noun[0] : noun[1]}</span> : null}
        {total != null && total > 0 ? <span>· {format.share(value, total)}</span> : null}
      </TipRow>
      {resolved ? <TipNote>{resolved}</TipNote> : null}
    </TipCard>
  );
};
